// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import { type AssetsComparison } from '@shared/data-access/interfaces/api/ai/assets';
import { type PortfolioAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import {
  type ReportType,
  type ResearchReportPayload,
  type ReportingPayload,
} from '@shared/data-access/interfaces/api/ai/reporting';
import {
  DocumentMimeType,
  type FileResponse,
} from '@shared/data-access/interfaces/api/commons';
import { type PortfolioInsight } from '@shared/data-access/interfaces/api/copilot/portfolio';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import { comparisonInsightsPayload } from '@shared/data-access/utils/payloads/ai';

export const reportingApi = aiApi.injectEndpoints({
  endpoints: build => ({
    portfolioAnalysis: build.query<
      FileResponse,
      { payload: ReportingPayload; assetType: string }
    >({
      query: ({ payload, assetType }) => ({
        url: `/reporting/portfolio/${assetType}/analysis/`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: {
        responseType: 'arraybuffer',
        mimeType: DocumentMimeType.Docx,
        fileName: /* istanbul ignore next */ args =>
          `${args.body.client_data.report_name}.docx`,
      },
      providesTags: ['Reporting', { type: 'Reporting', id: 'PortfolioAnalysis' }],
    }),

    portfolioInsights: build.query<
      PortfolioInsight[],
      { payload: PortfolioAnalysis; assetType: string }
    >({
      query: ({ payload, assetType }) => ({
        url: `/reporting/portfolio/${assetType}/insights/`,
        method: 'POST',
        body: payload,
      }),
      providesTags: ['Reporting', { type: 'Reporting', id: 'PortfolioInsights' }],
    }),

    comparisonInsights: build.query<PortfolioInsight[], AssetsComparison>({
      query: payload => ({
        url: `/reporting/assets/comparison/insights/`,
        method: 'POST',
        body: cleanPayload(payload, comparisonInsightsPayload),
      }),
      providesTags: [{ type: 'Reporting', id: 'ComparisonInsights' }],
    }),

    researchReport: build.query<
      FileResponse,
      { report_type: ReportType; payload: ResearchReportPayload }
    >({
      query: ({ report_type, payload }) => ({
        url: `/reporting/${report_type}/`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: {
        includeCurrency: true,
        responseType: 'arraybuffer',
        fileName: /* istanbul ignore next */ arg => `${arg.body.report_name}.pptx`,
        mimeType: DocumentMimeType.Pptx,
      },
      providesTags: [{ type: 'Reporting', id: 'ResearchReport' }],
    }),
  }),
});
